import { API_URL } from "~/lib/constants";

export type TimeRoundingMethod =
  "none" | "nearest_6_min" | "nearest_15_min" | "nearest_30_min" | "up_15_min";

export const TIME_ROUNDING_OPTIONS: Array<{
  label: string;
  value: TimeRoundingMethod;
}> = [
  { label: "Exact duration", value: "none" },
  { label: "Nearest 6 minutes (0.1h)", value: "nearest_6_min" },
  { label: "Nearest 15 minutes", value: "nearest_15_min" },
  { label: "Nearest 30 minutes", value: "nearest_30_min" },
  { label: "Round up to 15 minutes", value: "up_15_min" },
];

export function timeRoundingMethod(
  value: string | undefined,
): TimeRoundingMethod {
  if (
    value === "nearest_6_min" ||
    value === "nearest_15_min" ||
    value === "nearest_30_min" ||
    value === "up_15_min"
  ) {
    return value;
  }
  return "none";
}

export function timeRoundingLabel(
  value: TimeRoundingMethod | null | undefined,
) {
  return (
    TIME_ROUNDING_OPTIONS.find((option) => option.value === value)?.label ??
    "Exact duration"
  );
}

/** Round duration for billing. Mirrors SQL round_duration_seconds_for_invoice. */
export function roundDurationSecondsForInvoice(
  durationSeconds: number,
  method: TimeRoundingMethod,
): number {
  if (!Number.isFinite(durationSeconds) || durationSeconds <= 0) return 0;
  const minutes = durationSeconds / 60;
  let roundedMinutes = minutes;
  switch (method) {
    case "nearest_6_min":
      roundedMinutes = Math.round(minutes / 6) * 6;
      break;
    case "nearest_15_min":
      roundedMinutes = Math.round(minutes / 15) * 15;
      break;
    case "nearest_30_min":
      roundedMinutes = Math.round(minutes / 30) * 30;
      break;
    case "up_15_min":
      roundedMinutes = Math.ceil(minutes / 15) * 15;
      break;
    default:
      roundedMinutes = minutes;
  }
  if (roundedMinutes < 1) roundedMinutes = 1;
  return Math.max(Math.round(roundedMinutes * 60), 60);
}

export function normalizeBrandAccentHex(value: string | null | undefined) {
  const trimmed = value?.trim() ?? "";
  if (!trimmed) return null;
  const withHash = trimmed.startsWith("#") ? trimmed : `#${trimmed}`;
  if (!/^#[0-9A-Fa-f]{6}$/.test(withHash)) {
    throw new Error("Brand accent must be a hex colour like #0F766E.");
  }
  return withHash.toUpperCase();
}

export function workspaceLogoPublicUrl(logoPath: string | null | undefined) {
  if (!logoPath) return null;
  const base = API_URL.replace(/\/+$/, "");
  if (!base) return null;
  return `${base}/v1/logos/${logoPath}`;
}
